import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useMutation } from 'react-apollo';
import compose from 'lodash.flowright';
import { mapProps } from 'recompose';

import withModal from '../../../../../../components/global/modal/withModal';
import Button from '../../../../../../components/global/Button';
import { getErrorString } from '../../../../../../util/normalize-errors';
import { accountPaymentMethodCreate as accountPaymentMethodCreateMutation } from '../../../../../../graphql/mutations/checkout';
import Alert from '../../../../../../components/global/Alert';
import FormGroup from '../../../../../../components/global/FormGroup';
import { AccountPhotoManagerSubscriptions } from '../../../../../../graphql/queries/account';
import { sentryCaptureMessage } from '../../../../../../util/sentry';
import PromoCodeInput from '../../../../../../components/global/PromoCodeInput/PromoCodeInput';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../../constants/account-referrals';
import { PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD } from '../../../Account/AccountPhotoManagerSettings/components/PhotoManagerSubscriptionCheckout/helpers';

const propTypes = {
  className: PropTypes.string,
  onSuccess: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  description: PropTypes.node,
  buttonLabel: PropTypes.node.isRequired,
  supportsPromoCode: PropTypes.bool,
  onPromoCodeChange: PropTypes.func,
  onPromoCodeVerified: PropTypes.func,
};

const CARD_ELEMENT_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: '#438ed8',
      color: '#126ac3',
      fontWeight: 800,
      fontFamily: 'Muli, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',

      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: '#438ed8',
        fontWeight: 800,
      },
    },
    invalid: {
      iconColor: '#ea3770',
      color: '#ea3770',
    },
  },
};

const PhotoManagerCreditCardCaptureModal = ({
  className,
  onSuccess,
  onClose,
  title,
  description,
  buttonLabel,
  supportsPromoCode,
  billingPeriod = PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.YEARLY,
  onPromoCodeChange = () => {},
  onPromoCodeVerified = () => {},
}) => {
  const classes = classNames({
    'stripe-checkout-form-wrap': true,
    [className]: !!className,
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [responseError, setResponseError] = useState('');
  const [accountPaymentMethodCreate] = useMutation(
    accountPaymentMethodCreateMutation,
    {
      refetchQueries: [
        {
          query: AccountPhotoManagerSubscriptions,
        },
      ],
    },
  );
  const stripe = useStripe();
  const elements = useElements();
  const checkPromoCodeRef = useRef();
  const [shouldCheckPromoCode, setShouldCheckPromoCode] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    setSubmitting(true);
    setResponseError('');

    try {
      if (shouldCheckPromoCode && checkPromoCodeRef.current) {
        const success = await checkPromoCodeRef.current();
        if (!success) {
          setSubmitting(false);
          setResponseError('');
          return;
        }
      }

      const { error, token } = await stripe.createToken(
        elements.getElement(CardElement),
        {
          name,
        },
      );

      if (error) {
        throw Error(error.message);
      }

      await accountPaymentMethodCreate({
        variables: {
          input: {
            stripe_token: token.id,
            email_address: email,
          },
        },
      });
      setSubmitting(false);
      onSuccess?.({
        ...token,
        email,
      });
      onClose();
    } catch (error) {
      setResponseError(getErrorString(error));
      setSubmitting(false);

      sentryCaptureMessage('PRO CC Capture Error ', {
        level: 'error',
        extra: {
          error,
          email,
        },
      });
    }
  };

  return (
    <div className={classes}>
      {title}
      {description}

      <form onSubmit={handleSubmit} className="stripe-checkout-form">
        <fieldset className="stripe-checkout-form-fieldset">
          <FormGroup
            label="Name"
            labelSrOnly
            id="card-name"
            name="name"
            placeholder="Name on card"
            required
            value={name}
            onChange={event => {
              const { target } = event;
              const { value } = target;
              setName(value);
            }}
          />
          <FormGroup
            label="Email"
            labelSrOnly
            type="email"
            id="card-email"
            name="email"
            placeholder="Email"
            required
            value={email}
            onChange={event => {
              const { target } = event;
              const { value } = target;
              setEmail(value);
            }}
          />
        </fieldset>

        <fieldset className="stripe-checkout-form-fieldset stripe-checkout-form-card-fieldset">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </fieldset>

        {supportsPromoCode && (
          <fieldset className="stripe-checkout-form-fieldset stripe-checkout-form-promo-code-fieldset">
            <PromoCodeInput
              accountReferralTypes={[
                ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE,
                ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY,
                ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY,
              ]}
              labelSrOnly
              billingPeriod={billingPeriod}
              placeholder="Have a Promo Code?"
              onPromoCodeChange={onPromoCodeChange}
              onPromoCodeVerified={onPromoCodeVerified}
              onPromoCodeUpdate={(newPromoCode, isApplied, checkPromoCode) => {
                if (newPromoCode && !isApplied) {
                  setShouldCheckPromoCode(true);
                } else {
                  setShouldCheckPromoCode(false);
                }
                checkPromoCodeRef.current = checkPromoCode;
              }}
            />
          </fieldset>
        )}

        {responseError && (
          <Alert type="danger">
            <strong>Error:</strong> {responseError}
          </Alert>
        )}

        <div className="text-center mt-4">
          <Button
            type="submit"
            btnSize="lg"
            disabled={submitting}
            btnStyle="yellow"
          >
            {submitting ? 'Processing...' : buttonLabel}
          </Button>
        </div>
      </form>

      <div className="text-center">
        <Button
          type="button"
          btnStyle="link"
          className="mt-2"
          onClick={onClose}
          btnBlock
          disabled={submitting}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
};

PhotoManagerCreditCardCaptureModal.propTypes = propTypes;

export const PHOTO_MANAGER_CREDIT_CARD_CAPTURE_MODAL =
  'PHOTO_MANAGER_CREDIT_CARD_CAPTURE_MODAL';

export default withModal(
  compose(
    mapProps(({ onRequestClose, actionProps = {}, ...props }) => ({
      onClose: onRequestClose,
      ...actionProps,
      ...props,
    })),
  )(PhotoManagerCreditCardCaptureModal),
  {
    name: PHOTO_MANAGER_CREDIT_CARD_CAPTURE_MODAL,
    size: 'sm',
    type: 'full',
    contentLabel: '',
    hideHeader: true,
    className: 'stripe-checkout-modal',
    overlayClassName: 'stripe-checkout-modal-overlay z-modal-max',
  },
);
